/**
 * Quote carousel (FIL001-19 §6): moves on to the next quote every few seconds, and lets the dots pick one.
 *
 * The rotation runs only while it can be read and is not in the way. It waits until half the band is on screen
 * and while the tab is hidden; it pauses while a pointer or keyboard focus is on the band, and for the block the
 * theme editor has selected. It never starts under `prefers-reduced-motion`. A click on a dot stops it for good,
 * since the visitor has chosen what to read, and turns the live region polite so the new quote is announced.
 *
 * Guarded as a whole, like filliers-carousel.js: two quote sections on one page both emit this script.
 */
if (!customElements.get('filliers-quote-carousel')) {
  class FilliersQuoteCarousel extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.filliers-quote__slide')];
      this.dots = [...this.querySelectorAll('[data-quote-dot]')];
      this.region = this.querySelector('.filliers-quote__slides');
      if (this.slides.length < 2) return;

      this.index = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains('is-active'))
      );
      this.interval = (parseFloat(this.dataset.interval) || 8) * 1000;
      this.stopped = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.hovered = false;
      this.focused = false;
      this.pinned = false;
      this.visible = false;

      this.schedule = this.schedule.bind(this);

      this.dots.forEach((dot, index) => {
        dot.addEventListener('click', () => {
          this.stopped = true;
          this.region?.setAttribute('aria-live', 'polite');
          this.show(index);
          this.schedule();
        });
      });

      this.addEventListener('mouseenter', () => this.setHold('hovered', true));
      this.addEventListener('mouseleave', () => this.setHold('hovered', false));
      this.addEventListener('focusin', () => this.setHold('focused', true));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.setHold('focused', false);
      });

      // Theme editor: selecting a quote block shows it and holds it there until it is deselected.
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.indexOf(event.target.closest('.filliers-quote__slide'));
        if (index < 0) return;
        this.show(index);
        this.setHold('pinned', true);
      });
      this.addEventListener('shopify:block:deselect', () => this.setHold('pinned', false));

      document.addEventListener('visibilitychange', this.schedule);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.visible = entry.isIntersecting;
            this.schedule();
          },
          { threshold: 0.5 }
        );
        this.observer.observe(this);
      } else {
        this.visible = true;
        this.schedule();
      }
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      document.removeEventListener('visibilitychange', this.schedule);
    }

    setHold(reason, value) {
      this[reason] = value;
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      if (this.stopped || this.hovered || this.focused || this.pinned || !this.visible || document.hidden) return;

      this.timer = setTimeout(() => {
        this.show((this.index + 1) % this.slides.length);
        this.schedule();
      }, this.interval);
    }

    show(index) {
      this.index = index;
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      this.dots.forEach((dot, i) => {
        if (i === index) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }
  }

  customElements.define('filliers-quote-carousel', FilliersQuoteCarousel);
}
